/**
 * Os filtros rápidos da barra de Menções (2026-10-30, pedido da dona).
 *
 * Até aqui a barra tinha dois filtros fixos — a busca por título ou
 * conteúdo e a Polaridade — e todo o resto morava no "Mais filtros".
 * Agora cada pessoa escolhe o que fica na barra, entre TODOS os filtros
 * do painel (mais os dois de sempre), sem limite: pode pôr todos, e a
 * barra quebra em quantas linhas precisar. Busca e Polaridade são só o
 * padrão de quem nunca mexeu.
 *
 * **Como se escolhe** (mesmo dia, segundo pedido): cada filtro do "Mais
 * filtros" tem um ALFINETE — fixado aparece na barra, no fim; solto sai
 * dela. Na barra, cada filtro tem uma alça pra ARRASTAR até a posição
 * que a pessoa quiser (ponteiro, então mouse e toque; setas no teclado).
 * A ordem da lista é a ordem da barra.
 *
 * **Um filtro aparece na barra no máximo UMA vez** (regra da dona): a
 * lista é sempre saneada — duplicado, chave desconhecida (um filtro que
 * deixou de existir) e lixo do storage saem em `sanitizeQuickFilters`, e
 * `addQuickFilter` não acrescenta o que já está lá.
 *
 * **Mora no navegador** (`localStorage`), como as outras preferências de
 * vista da plataforma (`reputaition.uploads_view`, `why_text_open`): é
 * gosto de quem usa, não dado da empresa, e vale pra todas as empresas
 * que a pessoa abre. Storage ilegível = o padrão, sem erro.
 *
 * A barra edita o filtro VIVO (como a Polaridade sempre fez) — não o
 * rascunho do painel. Os dois leem o mesmo estado, então o que se marca
 * num aparece no outro.
 */

import { DIMENSION_LABELS, type Dimension } from "@/features/mentions/mention-filter-criteria";

/** Os filtros que não são faceta multi-seleção. */
export type ScalarQuickFilter =
  | "search"
  | "period"
  | "minCena"
  | "minAdValue"
  | "titleCitation"
  | "themeHighlight"
  | "sharedPost"
  | "reviewed"
  | "edited"
  | "onlyDiscarded"
  | "uploads"
  | "pastedRefs";

export type QuickFilterKey = ScalarQuickFilter | Dimension;

export type QuickFilterDefinition = {
  key: QuickFilterKey;
  label: string;
  /** O título do grupo, o mesmo do painel "Mais filtros". */
  group: string;
};

/** Na ordem e nos grupos do painel, pra quem escolhe achar cada um onde
 *  já está acostumado a ver. */
export const QUICK_FILTERS: readonly QuickFilterDefinition[] = [
  { key: "search", label: "Busca por título ou conteúdo", group: "Busca" },
  { key: "period", label: "Período", group: "Quando" },
  { key: "polarities", label: DIMENSION_LABELS.polarities, group: "O que foi dito" },
  { key: "approaches", label: DIMENSION_LABELS.approaches, group: "O que foi dito" },
  { key: "themes", label: DIMENSION_LABELS.themes, group: "O que foi dito" },
  { key: "themeGroups", label: DIMENSION_LABELS.themeGroups, group: "O que foi dito" },
  { key: "keyMessages", label: DIMENSION_LABELS.keyMessages, group: "O que foi dito" },
  { key: "themeHighlight", label: "Subtema em destaque", group: "O que foi dito" },
  { key: "minCena", label: "CENA mínimo", group: "O que foi dito" },
  { key: "brands", label: DIMENSION_LABELS.brands, group: "Quem & onde" },
  { key: "sources", label: DIMENSION_LABELS.sources, group: "Quem & onde" },
  { key: "spokespersons", label: DIMENSION_LABELS.spokespersons, group: "Quem & onde" },
  { key: "sharedPost", label: "Post em mais de um cliente", group: "Quem & onde" },
  { key: "vehicles", label: DIMENSION_LABELS.vehicles, group: "Só imprensa" },
  { key: "mediaTypes", label: DIMENSION_LABELS.mediaTypes, group: "Só imprensa" },
  { key: "tiers", label: DIMENSION_LABELS.tiers, group: "Só imprensa" },
  { key: "formats", label: DIMENSION_LABELS.formats, group: "Só imprensa" },
  { key: "minAdValue", label: "Valoração mínima", group: "Só imprensa" },
  { key: "titleCitation", label: "Citação no título", group: "Só imprensa" },
  { key: "channels", label: DIMENSION_LABELS.channels, group: "Só redes" },
  { key: "influencerTypes", label: DIMENSION_LABELS.influencerTypes, group: "Só redes" },
  { key: "publisherGroups", label: DIMENSION_LABELS.publisherGroups, group: "Só redes" },
  { key: "editors", label: DIMENSION_LABELS.editors, group: "Revisão" },
  { key: "reviewed", label: "Revisão", group: "Revisão" },
  { key: "edited", label: "Edição", group: "Revisão" },
  { key: "onlyDiscarded", label: "Só as descartadas", group: "Revisão" },
  { key: "clippingServices", label: DIMENSION_LABELS.clippingServices, group: "Origem" },
  { key: "uploads", label: "Uploads", group: "Origem" },
  { key: "pastedRefs", label: "Lista colada", group: "Links ou códigos colados" },
];

const KNOWN = new Set<string>(QUICK_FILTERS.map(filter => filter.key));

export const DEFAULT_QUICK_FILTERS: readonly QuickFilterKey[] = ["search", "polarities"];

export function quickFilterLabel(key: QuickFilterKey): string {
  return QUICK_FILTERS.find(filter => filter.key === key)?.label ?? key;
}

/** Só chaves conhecidas, cada uma uma vez, na ordem em que vieram. */
export function sanitizeQuickFilters(value: unknown): QuickFilterKey[] {
  if (!Array.isArray(value)) return [...DEFAULT_QUICK_FILTERS];
  const seen = new Set<string>();
  const keys: QuickFilterKey[] = [];
  for (const item of value) {
    if (typeof item !== "string" || !KNOWN.has(item) || seen.has(item)) continue;
    seen.add(item);
    keys.push(item as QuickFilterKey);
  }
  return keys;
}

/** Acrescenta no FIM — o que já está na barra não vai de novo. */
export function addQuickFilter(
  current: readonly QuickFilterKey[],
  key: QuickFilterKey
): QuickFilterKey[] {
  return current.includes(key) ? [...current] : [...current, key];
}

export function removeQuickFilter(
  current: readonly QuickFilterKey[],
  key: QuickFilterKey
): QuickFilterKey[] {
  return current.filter(item => item !== key);
}

/** O alfinete do painel: fixa no fim da barra, ou tira dela. */
export function togglePinnedQuickFilter(
  current: readonly QuickFilterKey[],
  key: QuickFilterKey
): QuickFilterKey[] {
  return current.includes(key) ? removeQuickFilter(current, key) : addQuickFilter(current, key);
}

/** Arrastar: põe `key` antes ou depois de `target`. Soltar em cima de si
 *  mesmo, ou chave que não está na barra, devolve a ordem como estava. */
export function moveQuickFilter(
  current: readonly QuickFilterKey[],
  key: QuickFilterKey,
  target: QuickFilterKey,
  side: "before" | "after"
): QuickFilterKey[] {
  if (key === target || !current.includes(key) || !current.includes(target)) return [...current];
  const rest = current.filter(item => item !== key);
  const at = rest.indexOf(target) + (side === "after" ? 1 : 0);
  return [...rest.slice(0, at), key, ...rest.slice(at)];
}

/** Teclado: uma casa pra trás (`-1`) ou pra frente (`+1`), parando nas
 *  pontas. */
export function shiftQuickFilter(
  current: readonly QuickFilterKey[],
  key: QuickFilterKey,
  delta: -1 | 1
): QuickFilterKey[] {
  const index = current.indexOf(key);
  const to = index + delta;
  if (index < 0 || to < 0 || to >= current.length) return [...current];
  const next = [...current];
  [next[index], next[to]] = [next[to], next[index]];
  return next;
}

const STORAGE_KEY = "reputaition.mention_quick_filters";

export function readQuickFilters(): QuickFilterKey[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === null ? [...DEFAULT_QUICK_FILTERS] : sanitizeQuickFilters(JSON.parse(raw));
  } catch {
    return [...DEFAULT_QUICK_FILTERS];
  }
}

export function writeQuickFilters(keys: readonly QuickFilterKey[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitizeQuickFilters(keys)));
  } catch {
    // Sem storage: a escolha vale até sair da tela.
  }
}
